import { FC } from 'react';

import { useFormik } from 'formik';
import { values } from 'remeda';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';

import { SlotForm, validationSchema } from './form';

import { CheckBoxAutocomplete } from 'v2/components/autocomplete/checkBoxAutocomplete/CheckBoxAutocomplete';
import { FileSelector } from 'v2/components/fileSystem/fileSelector/FileSelector';
import { Select } from 'v2/components/select/Select';
import { Switch } from 'v2/components/switch/Switch';
import { ProviderName, SlotMark } from 'v2/constants';
import { enumToSelect } from 'v2/utils';

interface SlotFormDialogProps {
  title: string;
  isOpen: boolean;
  onClose: VoidFunction;
  initialValues: SlotForm;
  isSubmitting: boolean;
  onSubmit: (values: SlotForm, resetForm: VoidFunction) => void;
  isProviderSlot?: boolean;
}

export const SlotFormDialog: FC<SlotFormDialogProps> = ({
  title,
  isOpen,
  onClose,
  initialValues,
  isSubmitting,
  onSubmit,
  isProviderSlot,
}) => {
  const formik = useFormik<SlotForm>({
    initialValues,
    validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values, { resetForm }) => {
      onSubmit(values, resetForm);
    },
    enableReinitialize: true,
  });

  const handleSelectProvider = (value: ProviderName) => {
    formik.setFieldValue('providerName', value);
  };

  const handleChangeMarks = (values: SlotMark[]) => {
    formik.setFieldValue('marks', values);
  };

  const handleSelectImage = (field: keyof SlotForm) => (value: string) => {
    formik.setFieldValue(field, value);
  };

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };
  return (
    <Dialog open={isOpen} onClose={handleClose} scroll="paper" maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>

      <DialogContent>
        <form id="slotForm" className="form with-top-space" onSubmit={formik.handleSubmit}>
          <Select
            value={formik.values.providerName}
            options={enumToSelect(ProviderName)}
            onChange={handleSelectProvider}
            label="Provider"
            required
            disabled={isProviderSlot}
          />

          <TextField
            name="identifier"
            value={formik.values.identifier}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Identifier"
            required
            disabled={isProviderSlot}
          />

          <TextField
            name="title"
            value={formik.values.title}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Title"
            required
          />

          <CheckBoxAutocomplete
            label="Marks"
            value={formik.values.marks}
            options={values(SlotMark)}
            onChange={handleChangeMarks}
          />

          <TextField
            name="order"
            value={formik.values.order}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Order"
            type="number"
            required
          />

          <div className="form-group">
            <FileSelector
              value={formik.values.iconId}
              onSelect={handleSelectImage('iconId')}
              error={formik.errors.iconId}
              label="Icon"
              required
            />

            <FileSelector
              value={formik.values.mobileIconId ?? ''}
              onSelect={handleSelectImage('mobileIconId')}
              label="Mobile Icon"
            />
          </div>

          <Switch name="isActive" checked={formik.values.isActive} onChange={formik.handleChange} label="Active" />
        </form>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="error" variant="outlined">
          Cancel
        </Button>

        <Button form="slotForm" type="submit" variant="contained" loading={isSubmitting}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
